// =============================================================================
// Create Tenant Modal - Creazione nuova organizzazione
// =============================================================================
// Flusso a due step: 1) scelta del template iniziale (preset con tipi di
// risorsa e servizi già configurati, oppure organizzazione vuota); 2) dati
// dell'organizzazione (nome + slug).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Briefcase,
  Building2,
  Check,
  Loader2,
  Scissors,
  Stethoscope,
  Wrench,
  X,
  type LucideIcon,
} from 'lucide-react';
import { apiClient } from '../api/client';
import { useTenantPresets, type TenantPresetSummary } from '../api/hooks';
import { useTenantStore } from '../store/tenantStore';
import { useToast } from '../store/toastStore';
import { generateSlug } from '../lib/utils';

interface CreateTenantModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Registry icone lucide per i preset (il backend invia il nome dell'icona)
const PRESET_ICONS: Record<string, LucideIcon> = {
  Stethoscope,
  Wrench,
  Scissors,
  Briefcase,
};

const EMPTY_PRESET_ID = 'empty';

type Step = 'template' | 'details';

export function CreateTenantModal({ isOpen, onClose }: CreateTenantModalProps) {
  const { t } = useTranslation('common');
  const { memberships, setMemberships, selectTenant } = useTenantStore();
  const toast = useToast();

  const [step, setStep] = useState<Step>('template');
  const [selectedPresetId, setSelectedPresetId] = useState<string>(EMPTY_PRESET_ID);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const presetsQuery = useTenantPresets(isOpen);

  if (!isOpen) return null;

  const presets = presetsQuery.data ?? [];

  const handleNameChange = (value: string) => {
    setName(value);
    setSlug(generateSlug(value));
  };

  const handleClose = () => {
    if (isLoading) return;
    setStep('template');
    setSelectedPresetId(EMPTY_PRESET_ID);
    setName('');
    setSlug('');
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !slug.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const payload: Record<string, string> = { name, slug };
      if (selectedPresetId !== EMPTY_PRESET_ID) {
        payload.presetId = selectedPresetId;
      }
      const response = await apiClient.post('/tenants', payload);
      const tenant = response.data;

      const newMembership = {
        organizationId: tenant.id,
        tenantName: tenant.name,
        tenantSlug: tenant.slug,
        timezone: tenant.timezone,
        role: 'OWNER' as const,
      };

      // Aggiorna memberships e seleziona il nuovo tenant
      setMemberships([...memberships, newMembership]);
      selectTenant(newMembership);

      toast.success(t('tenant.createSuccess'));
      window.location.href = '/dashboard';
    } catch (err: any) {
      const msg =
        err.response?.data?.message || t('tenant.createError');
      setError(typeof msg === 'string' ? msg : msg[0]);
    } finally {
      setIsLoading(false);
    }
  };

  const presetName = (id: string) => t(`tenant.presets.${id}.name`);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className={`relative bg-white rounded-xl shadow-2xl w-full mx-4 overflow-hidden ${
          step === 'template' ? 'max-w-2xl' : 'max-w-md'
        }`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-white" />
            <div>
              <h2 className="text-lg font-semibold text-white">
                {t('tenant.createTitle')}
              </h2>
              <p className="text-xs text-emerald-50">
                {step === 'template'
                  ? t('tenant.stepTemplate')
                  : t('tenant.stepDetails')}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 'template' ? (
          // ===================================================================
          // Step 1: scelta template
          // ===================================================================
          <div className="p-6">
            <h3 className="text-base font-semibold text-gray-900">
              {t('tenant.chooseTemplate')}
            </h3>
            <p className="text-sm text-gray-500 mt-1 mb-4">
              {t('tenant.chooseTemplateHint')}
            </p>

            {presetsQuery.isLoading ? (
              <div className="flex items-center justify-center py-10 text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[50vh] overflow-y-auto pr-1">
                {/* Organizzazione vuota */}
                <TemplateCard
                  isSelected={selectedPresetId === EMPTY_PRESET_ID}
                  onSelect={() => setSelectedPresetId(EMPTY_PRESET_ID)}
                  icon={Building2}
                  iconColor="#6B7280"
                  title={t('tenant.emptyPresetName')}
                  description={t('tenant.emptyPresetDescription')}
                />

                {presets.map((preset) => (
                  <TemplateCard
                    key={preset.id}
                    isSelected={selectedPresetId === preset.id}
                    onSelect={() => setSelectedPresetId(preset.id)}
                    icon={PRESET_ICONS[preset.icon] ?? Building2}
                    iconColor={preset.resourceTypes[0]?.color ?? '#10B981'}
                    title={presetName(preset.id)}
                    description={t(`tenant.presets.${preset.id}.description`)}
                    preset={preset}
                    resourcesLabel={t('tenant.presetIncludesResources')}
                    servicesLabel={t('tenant.presetIncludesServices')}
                  />
                ))}
              </div>
            )}

            {presetsQuery.isError && (
              <p className="text-xs text-amber-600 mt-3">
                {t('tenant.presetsLoadError')}
              </p>
            )}

            <div className="flex items-center justify-end gap-3 pt-5">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                {t('buttons.cancel')}
              </button>
              <button
                type="button"
                onClick={() => setStep('details')}
                className="btn-primary px-4 py-2 text-sm"
              >
                {t('buttons.continue')}
              </button>
            </div>
          </div>
        ) : (
          // ===================================================================
          // Step 2: dati organizzazione
          // ===================================================================
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Riepilogo template scelto */}
            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Check className="w-4 h-4 text-emerald-600" />
                <span>
                  {t('tenant.selectedTemplate')}:{' '}
                  <span className="font-medium">
                    {selectedPresetId === EMPTY_PRESET_ID
                      ? t('tenant.emptyPresetName')
                      : presetName(selectedPresetId)}
                  </span>
                </span>
              </div>
              <button
                type="button"
                onClick={() => setStep('template')}
                disabled={isLoading}
                className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
              >
                {t('buttons.edit')}
              </button>
            </div>

            {/* Nome */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('tenant.companyName')}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder={t('tenant.companyNamePlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                autoFocus
                disabled={isLoading}
              />
            </div>

            {/* Slug */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('tenant.slug')}
              </label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase())}
                placeholder={t('tenant.slugPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono"
                disabled={isLoading}
              />
              <p className="text-xs text-gray-400 mt-1">
                {t('tenant.slugHint')}
              </p>
            </div>

            {/* Errore */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">
                {error}
              </div>
            )}

            {/* Azioni */}
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setStep('template')}
                className="flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                disabled={isLoading}
              >
                <ArrowLeft className="w-4 h-4" />
                {t('buttons.back')}
              </button>
              <button
                type="submit"
                disabled={isLoading || !name.trim() || !slug.trim()}
                className="flex-1 flex items-center justify-center gap-2 btn-primary px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('tenant.creating')}
                  </>
                ) : (
                  t('tenant.createButton')
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Card di selezione template
// =============================================================================

interface TemplateCardProps {
  isSelected: boolean;
  onSelect: () => void;
  icon: LucideIcon;
  iconColor: string;
  title: string;
  description: string;
  preset?: TenantPresetSummary;
  resourcesLabel?: string;
  servicesLabel?: string;
}

function TemplateCard({
  isSelected,
  onSelect,
  icon: Icon,
  iconColor,
  title,
  description,
  preset,
  resourcesLabel,
  servicesLabel,
}: TemplateCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`relative text-left border rounded-xl p-4 transition-all ${
        isSelected
          ? 'border-emerald-500 ring-2 ring-emerald-500/30 bg-emerald-50/50'
          : 'border-gray-200 hover:border-emerald-300 hover:bg-gray-50'
      }`}
    >
      {isSelected && (
        <span className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
          <Check className="w-3.5 h-3.5 text-white" />
        </span>
      )}

      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center text-white shrink-0"
          style={{ backgroundColor: iconColor }}
        >
          <Icon className="w-5 h-5" />
        </div>
        <span className="font-medium text-sm text-gray-900 pr-5">{title}</span>
      </div>

      <p className="text-xs text-gray-500 mb-2">{description}</p>

      {preset && (
        <div className="space-y-1">
          <p className="text-xs text-gray-600">
            <span className="font-medium">{resourcesLabel}:</span>{' '}
            {preset.resourceTypes.map((rt) => rt.name).join(', ')}
          </p>
          <p className="text-xs text-gray-600">
            <span className="font-medium">{servicesLabel}:</span>{' '}
            {preset.services.map((s) => s.name).join(', ')}
          </p>
        </div>
      )}
    </button>
  );
}
